/* Public parent booking page — iter84.
 * Route: /book-plan-appointment/:token (no auth wrapper)
 * Parent lands from the notification email, picks a teacher + date/time,
 * and submits without any login. Backend verifies the signed token. */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  CalendarCheck, Warning, CheckCircle, ChalkboardTeacher,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicPlanBooking() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);

  const [teacherId, setTeacherId] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/public/plan-booking/${token}`);
        setInfo(data);
        if ((data.teachers || []).length) {
          setTeacherId(data.teachers[0].id);
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'Lien invalide ou expiré');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!teacherId || !date || !time) {
      toast.error('Veuillez remplir tous les champs'); return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/public/plan-booking/${token}/book`,
        { teacher_id: teacherId, date, time, reason },
      );
      setBooked(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de réservation');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <p className="text-[#94A3B8]">Chargement…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3"
                          data-testid="pb-error-card">
            <Warning size={48} className="mx-auto text-red-600"
                        weight="duotone" />
            <h1 className="text-xl font-outfit font-bold text-[#0F172A]">
              Lien inaccessible
            </h1>
            <p className="text-sm text-[#64748B]">{error}</p>
            <p className="text-xs text-[#94A3B8]">
              Le lien envoyé par email est valable 14 jours. Contactez
              directement l'école si votre lien est expiré.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (booked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3"
                          data-testid="pb-success-card">
            <CheckCircle size={64} className="mx-auto text-emerald-600"
                            weight="duotone" />
            <h1 className="text-2xl font-outfit font-bold text-emerald-700">
              Rendez-vous confirmé
            </h1>
            <p className="text-sm text-[#0F172A]">
              Votre rendez-vous avec <strong>{booked.teacher_name}</strong>{' '}
              est confirmé le <strong>{booked.date} à {booked.time}</strong>.
            </p>
            <p className="text-xs text-[#64748B]">
              L'enseignant a été notifié. Vous pouvez fermer cette page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 flex items-start justify-center">
      <Toaster position="top-center" richColors />
      <Card className="max-w-lg w-full mt-8" data-testid="pb-page">
        <CardContent className="p-6 space-y-5">
          <div className="text-center">
            <CalendarCheck size={40} weight="duotone"
                            className="mx-auto text-[#0052FF]" />
            <h1 className="text-2xl font-outfit font-bold mt-2">
              Prendre rendez-vous
            </h1>
            <p className="text-sm text-[#64748B]">{info.school_name}</p>
          </div>

          <div className="p-3 rounded-lg bg-[#EEF2FF] border border-[#C7D2FE] text-sm">
            <p>Bonjour <strong>{info.parent_name}</strong>,</p>
            <p className="mt-1">
              Vous prenez rendez-vous concernant le plan de soutien
              pédagogique de <strong>{info.student_name}</strong>{' '}
              ({info.class_name}).
            </p>
            {info.overall_mean != null && (
              <p className="text-xs text-[#64748B] mt-1">
                Moyenne actuelle : <strong>{info.overall_mean}/20</strong>
              </p>
            )}
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Enseignant</Label>
              <Select value={teacherId} onValueChange={setTeacherId}>
                <SelectTrigger data-testid="pb-teacher-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(info.teachers || []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="flex items-center gap-2">
                        <ChalkboardTeacher size={14} />
                        {t.name} — <em className="text-[#64748B]">{t.subject}</em>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(info.teachers || []).length === 0 && (
                <p className="text-xs text-red-600 mt-1">
                  Aucun enseignant disponible. Contactez directement l'école.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={date}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDate(e.target.value)}
                  data-testid="pb-date-input" required />
              </div>
              <div>
                <Label>Heure</Label>
                <Input type="time" value={time}
                  onChange={(e) => setTime(e.target.value)}
                  data-testid="pb-time-input" required />
              </div>
            </div>

            <div>
              <Label>Message pour l'enseignant (facultatif)</Label>
              <Textarea rows={3} value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Sujets à aborder, contraintes horaires, …"
                data-testid="pb-reason-input" />
            </div>

            <Button type="submit" disabled={submitting}
              className="w-full bg-[#0052FF] hover:bg-[#0044CC] text-white h-11"
              data-testid="pb-submit-btn">
              {submitting ? 'Réservation…' : '✓ Confirmer le rendez-vous'}
            </Button>

            <p className="text-[10px] text-center text-[#94A3B8]">
              Aucun compte requis. Ce lien personnel est valable 14 jours.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
